import React, { useState, useEffect } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Button, IconButton, List, ListItemButton, ListItemText,
  Paper, Stack, Chip, Snackbar, Alert, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RateReviewIcon from '@mui/icons-material/RateReview';
import UndoIcon from '@mui/icons-material/Undo';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { getBlock } from '@atlas/blocks';
import { PropForm } from './PropForm';
import { SeoDialog, type Seo } from './SeoDialog';
import { Preview } from './Preview';
import { SectionPicker } from './SectionPicker';
import { starterProps, withSchemaFields } from './starter';
import { PUBLISH_ROLES, REVIEW_ROLES, type Api, type PageForEdit, type TenantView, type Block } from './api';

const REV_CHIP: Record<string, { label: string; color: 'default' | 'warning' | 'success' }> = {
  DRAFT: { label: 'Draft', color: 'default' },
  IN_REVIEW: { label: 'In review', color: 'warning' },
  PUBLISHED: { label: 'Published', color: 'success' },
};

const ACRONYMS: Record<string, string> = { cta: 'CTA', seo: 'SEO', faq: 'FAQ' };
export function blockLabel(type: string): string {
  return type
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/\s+/)
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
function summarize(b: Block): string {
  const p = (b.props ?? {}) as Record<string, unknown>;
  for (const k of ['heading', 'title', 'eyebrow', 'label', 'name', 'text']) {
    if (typeof p[k] === 'string' && p[k]) return p[k] as string;
  }
  return '';
}

const isLocalHost = (h: string) => h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');

// Resolve the "View live" URL from the TENANT'S OWN public domain, so each brand
// opens its own site and it's correct in every environment automatically (the
// Domain rows differ per env: localhost in dev, the real hosts in staging/prod).
// In local dev we prefer a *.localhost host (served by the dev server on :3000);
// in a deployed env we prefer the real (non-localhost) host over https.
// Fallbacks when a tenant has no domain on record: ATLAS_WEB_URL (build-time env,
// injected like ATLAS_API_URL), else http://localhost:3000.
function viewLiveUrl(configUrl: string | null | undefined, hosts: string[] | undefined, slug: string): string {
  const path = slug === 'home' ? '' : slug;
  // A Super-Admin-set marketing URL (Tenant Setup) wins over domain-derivation.
  if (configUrl && configUrl.trim()) return `${configUrl.trim().replace(/\/+$/, '')}/${path}`;
  const list = hosts ?? [];
  const devCtx = typeof window !== 'undefined' && isLocalHost(window.location.hostname);
  const host = devCtx
    ? list.find(isLocalHost) ?? list.find((h) => !isLocalHost(h))
    : list.find((h) => !isLocalHost(h)) ?? list[0];
  if (!host) {
    const base = ((import.meta.env.ATLAS_WEB_URL as string) || 'http://localhost:3000').replace(/\/+$/, '');
    return `${base}/${path}`;
  }
  const local = isLocalHost(host);
  return `${local ? 'http' : 'https'}://${host}${local ? ':3000' : ''}/${path}`;
}

export function Editor({
  api, tenant, page, onBack,
}: { api: Api; tenant: TenantView; page: PageForEdit; onBack: () => void }) {
  const [blocks, setBlocks] = useState<Block[]>(page.blocks);
  // Tenant Setup config (for the "View live" target). Non-secret; any member can read.
  const [marketingUrl, setMarketingUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.tenantConfig(tenant.tenantId).then((c) => { if (alive) setMarketingUrl(c.marketingUrl); }).catch(() => {});
    return () => { alive = false; };
  }, [tenant.tenantId]);
  const [sel, setSel] = useState<number>(page.blocks.length ? 0 : -1);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<'' | 'save' | 'publish' | 'submit' | 'reject'>('');
  const [revStatus, setRevStatus] = useState(page.revisionStatus);
  const [addOpen, setAddOpen] = useState(false);
  const [seo, setSeoState] = useState<Seo>((page.seo as Seo) ?? {});
  const [seoOpen, setSeoOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const canPublish = PUBLISH_ROLES.includes(tenant.role); // publish directly
  const canReview = REVIEW_ROLES.includes(tenant.role); // approve / request changes
  const isAuthor = !canReview; // author/contributor: submit for review only

  const mutate = (next: Block[], nextSel = sel) => { setBlocks(next); setSel(nextSel); setDirty(true); };
  const setProps = (i: number, props: Record<string, unknown>) =>
    mutate(blocks.map((b, j) => (j === i ? { ...b, props } : b)));
  const move = (i: number, d: number) => {
    const n = [...blocks]; const tmp = n[i]!; n[i] = n[i + d]!; n[i + d] = tmp; mutate(n, i + d);
  };
  const remove = (i: number) => mutate(blocks.filter((_, j) => j !== i), Math.max(-1, Math.min(sel, blocks.length - 2)));
  const add = (type: string, previewProps?: Record<string, unknown>) => {
    const def = getBlock(type);
    // Seed with the content shown in the picker preview (real "as-seen" content
    // when available) so the section is visible immediately; fall back to
    // schema-derived starter props. Deep-clone to avoid sharing the picker's
    // cached object.
    const seeded = previewProps && Object.keys(previewProps).length
      ? (JSON.parse(JSON.stringify(previewProps)) as Record<string, unknown>)
      : def ? starterProps(def.schema) : {};
    mutate([...blocks, { type, props: seeded }], blocks.length);
    setAddOpen(false);
  };

  async function save(): Promise<string | null> {
    setBusy('save');
    try {
      const { revisionId } = await api.saveDraft(tenant.tenantId, page.id, blocks, seo);
      setDirty(false);
      setRevStatus('DRAFT'); // a saved edit is a fresh draft
      setToast({ msg: 'Draft saved', sev: 'success' });
      return revisionId;
    } catch (e: any) {
      setToast({ msg: e.details ? `Can’t save: ${e.details[0]}` : `Save failed: ${e.message}`, sev: 'error' });
      return null;
    } finally {
      setBusy('');
    }
  }
  async function publish() {
    setBusy('publish');
    try {
      if (dirty && !(await save())) return; // save failed
      await api.publish(tenant.tenantId, page.id);
      setRevStatus('PUBLISHED');
      setToast({ msg: 'Published — live on the site', sev: 'success' });
    } catch (e: any) {
      setToast({ msg: `Publish failed: ${e.message}`, sev: 'error' });
    } finally {
      setBusy('');
    }
  }
  async function submit() {
    setBusy('submit');
    try {
      if (dirty && !(await save())) return;
      await api.submit(tenant.tenantId, page.id);
      setRevStatus('IN_REVIEW');
      setToast({ msg: 'Submitted for review', sev: 'success' });
    } catch (e: any) {
      setToast({ msg: `Submit failed: ${e.message}`, sev: 'error' });
    } finally {
      setBusy('');
    }
  }
  async function reject() {
    setBusy('reject');
    try {
      await api.requestChanges(tenant.tenantId, page.id);
      setRevStatus('DRAFT');
      setToast({ msg: 'Sent back to the author', sev: 'success' });
    } catch (e: any) {
      setToast({ msg: `Failed: ${e.message}`, sev: 'error' });
    } finally {
      setBusy('');
    }
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <IconButton edge="start" onClick={onBack}><ArrowBackIcon /></IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>/{page.slug}</Typography>
          <Chip size="small" label={(REV_CHIP[revStatus] ?? { label: revStatus }).label} color={(REV_CHIP[revStatus] ?? { color: 'default' as const }).color} sx={{ ml: 1 }} />
          {dirty ? <Chip size="small" label="Unsaved" color="warning" sx={{ ml: 1 }} /> : null}
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" startIcon={<TravelExploreIcon />} onClick={() => setSeoOpen(true)}>
            SEO
          </Button>
          <Button size="small" startIcon={<OpenInNewIcon />} href={viewLiveUrl(marketingUrl, tenant.hosts, page.slug)} target="_blank" sx={{ ml: 1 }}>
            View live
          </Button>
          <Button size="small" startIcon={<SaveIcon />} onClick={save} disabled={!dirty || !!busy} sx={{ ml: 1 }}>
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </Button>

          {canPublish ? (
            <Button size="small" variant="contained" startIcon={<PublishIcon />} onClick={publish} disabled={!!busy} sx={{ ml: 1 }}>
              {busy === 'publish' ? 'Publishing…' : 'Publish'}
            </Button>
          ) : canReview ? (
            revStatus === 'IN_REVIEW' ? (
              <>
                <Button size="small" startIcon={<UndoIcon />} onClick={reject} disabled={!!busy} sx={{ ml: 1 }}>
                  {busy === 'reject' ? '…' : 'Request changes'}
                </Button>
                <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={publish} disabled={!!busy} sx={{ ml: 1 }}>
                  {busy === 'publish' ? 'Publishing…' : 'Approve & publish'}
                </Button>
              </>
            ) : null
          ) : revStatus === 'IN_REVIEW' ? (
            <Chip size="small" color="warning" label="Submitted for review" sx={{ ml: 1 }} />
          ) : (
            <Button size="small" variant="contained" startIcon={<RateReviewIcon />} onClick={submit} disabled={!!busy} sx={{ ml: 1 }}>
              {busy === 'submit' ? 'Submitting…' : 'Submit for review'}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, display: 'flex', minHeight: 0 }}>
        {/* Sections list */}
        <Paper square elevation={0} sx={{ width: 280, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1.5, pb: 1 }}>
            <Typography variant="overline" color="text.secondary">Sections</Typography>
          </Box>
          <List dense sx={{ flexGrow: 1, pt: 0 }}>
            {blocks.map((b, i) => (
              <ListItemButton key={i} selected={i === sel} onClick={() => setSel(i)} sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={blockLabel(b.type)}
                  secondary={summarize(b)}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                  secondaryTypographyProps={{ noWrap: true, fontSize: 12 }}
                />
                <Stack direction="row" onClick={(e) => e.stopPropagation()}>
                  <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                  <IconButton size="small" disabled={i === blocks.length - 1} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => remove(i)}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
                </Stack>
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add section</Button>
          </Box>
        </Paper>

        {/* Live preview */}
        <Box sx={{ flexGrow: 1, minWidth: 0, bgcolor: 'background.default' }}>
          <Preview blocks={blocks} />
        </Box>

        {/* Field editor for the selected section */}
        <Paper square elevation={0} sx={{ width: 380, borderLeft: '1px solid', borderColor: 'divider', overflowY: 'auto', p: 2 }}>
          {sel >= 0 && blocks[sel] ? (
            <>
              <Typography variant="overline" color="text.secondary">Editing</Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>{blockLabel(blocks[sel].type)}</Typography>
              <PropForm
                props={withSchemaFields(getBlock(blocks[sel].type)?.schema, (blocks[sel].props ?? {}) as Record<string, unknown>)}
                onChange={(p) => setProps(sel, p)}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Select a section to edit, or add one.</Typography>
          )}
        </Paper>
      </Box>

      {/* Add-section picker (list + live preview using real page content) */}
      <SectionPicker open={addOpen} onClose={() => setAddOpen(false)} onPick={add} api={api} tenantId={tenant.tenantId} />
      <SeoDialog open={seoOpen} onClose={() => setSeoOpen(false)} seo={seo} slug={page.slug} onChange={(s) => { setSeoState(s); setDirty(true); }} />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
